import { Box, MenuItem, Typography, TextField, Button } from "@material-ui/core";
import LyfeTextField from "libs/react-common/src/TextField/TextField";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import LyfeAgreementTag from "../../../../components/LyfeAgreementTag";
import { useDataMutation } from "../../../../hooks/useDataModule";
import { SelectableValue } from "../../../../models/common/selectable-value";
import {
	assigneeStatusDescriptions,
	assigneeStatuses,
	assigneeStatusLabels,
	roles,
	userRoleDescriptions,
	userRoleLabels,
} from "../../../../models/common/user-roles";
import { APPLY_DEFAULT_USER_ROLES_TO_ALL, UPDATE_DEFAULT_USER_ROLE } from "../queries";
import useOrganizationStyles from "../use-organization-styles";
import { GetDefaultAgreementRolesQuery } from "../types/queries.generated";
import { AgreementUserRole, AssigneeStatus } from "apps/platform-client/client/src/models/generated/graphql";

interface DefaultAgreementRolesRowProps {
	index: number;
	groupId: number;
	orgId: string;
	user: GetDefaultAgreementRolesQuery["GetDefaultAgreementRoles"][0];
	refetch: () => void;
}

const DefaultAgreementRolesRow: React.FC<DefaultAgreementRolesRowProps> = ({ index, user, groupId, refetch }) => {
	const { t } = useTranslation();
	const { menuItemContent, optionDescription, applyToAllButton } = useOrganizationStyles();
	const [caseAssigneeOptions, setCaseAssigneeOptions] = useState<Array<SelectableValue>>(assigneeStatuses);
	const [documentAssigneeOptions, setDocumentAssigneeOptions] = useState<Array<SelectableValue>>(assigneeStatuses);
	const [currentUser, setCurrentUser] = useState<GetDefaultAgreementRolesQuery["GetDefaultAgreementRoles"][0]>(user);
	const [applyToAllItems, setApplyToAllItems] = useState<boolean>(true);
	const mutationsVariable = {
		onCompleted: () => {
			refetch();
		},
	};
	const [mutate] = useDataMutation(UPDATE_DEFAULT_USER_ROLE, mutationsVariable);
	const [applyToAll] = useDataMutation(APPLY_DEFAULT_USER_ROLES_TO_ALL);

	const assigneeStatus: Array<SelectableValue> = [
		{ label: t("yes"), value: "yes" },
		{ label: t("no"), value: "no" },
	];
	const [assigneeStatusOptions, setAssigneeStatusOptions] = useState<Array<SelectableValue>>(assigneeStatus);

	const supperRoles = [AgreementUserRole.Approver, AgreementUserRole.Editor, AgreementUserRole.Owner];
	useEffect(() => {
		if (currentUser && !applyToAllItems) {
			mutate({
				variables: {
					input: {
						userId: currentUser.userId,
						role: currentUser.role,
						agreementTag: currentUser.tagType,
						agreementAssignee: currentUser.isAgreementAssignee,
						caseAssignee: currentUser.caseAssignee,
						documentAssignee: currentUser.documentAssignee,
					},
				},
			});
		}
	}, [currentUser]);

	useEffect(() => {
		if (user) {
			setCurrentUser(user);
		}
	}, [user]);

	function handleUserAssigneeChanges(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
		if (!event.target.value) {
			return;
		}
		setApplyToAllItems(false);
		setCurrentUser({ ...currentUser, isAgreementAssignee: event.target.value === "yes" });
	}

	function isDisabled() {
		return currentUser.role === AgreementUserRole.LimitedAccessUser || currentUser.role === AgreementUserRole.FullAccessUser;
	}

	function handleCaseAssigneeChanges(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
		if (!event.target.value) {
			return;
		}
		setApplyToAllItems(false);
		setCurrentUser({
			...currentUser,
			caseAssignee: event.target.value as AssigneeStatus,
		});
	}

	function handleDocumentAssigneeChanges(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
		if (!event.target.value) {
			return;
		}

		setApplyToAllItems(false);
		setCurrentUser({
			...currentUser,
			documentAssignee: event.target.value as AssigneeStatus,
		});
	}

	function handleUserChanges(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
		if (!event.target.value) {
			return;
		}
		const isPrevSupperRole = supperRoles.includes(currentUser.role);

		setApplyToAllItems(false);
		let updatedUser: GetDefaultAgreementRolesQuery["GetDefaultAgreementRoles"][0] = {
			...currentUser,
			isAgreementAssignee: true,
			caseAssignee: isPrevSupperRole ? currentUser.caseAssignee : AssigneeStatus.No,
			documentAssignee: isPrevSupperRole ? currentUser.documentAssignee : AssigneeStatus.No,
			role: event.target.value as AgreementUserRole,
		};

		if (event.target.value === AgreementUserRole.LimitedAccessUser || event.target.value === AgreementUserRole.FullAccessUser) {
			setAssigneeStatusOptions(assigneeStatusOptions.filter((opt: SelectableValue) => opt.value === "no"));
			setCaseAssigneeOptions(caseAssigneeOptions.filter((opt: SelectableValue) => opt.value === AssigneeStatus.No));
			setDocumentAssigneeOptions(documentAssigneeOptions.filter((opt: SelectableValue) => opt.value === AssigneeStatus.No));

			updatedUser = {
				...currentUser,
				isAgreementAssignee: false,
				caseAssignee: AssigneeStatus.No,
				documentAssignee: AssigneeStatus.No,
				role: event.target.value as AgreementUserRole,
			};
		} else if (event.target.value === AgreementUserRole.Editor) {
			setAssigneeStatusOptions(assigneeStatusOptions.filter((f: SelectableValue) => f.value === "no"));
			setCaseAssigneeOptions(assigneeStatuses);
			setDocumentAssigneeOptions(assigneeStatuses);
			updatedUser = {
				...currentUser,
				isAgreementAssignee: false,
				caseAssignee: isPrevSupperRole ? currentUser.caseAssignee : AssigneeStatus.No,
				documentAssignee: isPrevSupperRole ? currentUser.documentAssignee : AssigneeStatus.No,
				role: event.target.value as AgreementUserRole,
			};
		} else {
			setAssigneeStatusOptions(assigneeStatus);
			setCaseAssigneeOptions(assigneeStatuses);
			setDocumentAssigneeOptions(assigneeStatuses);
		}
		setCurrentUser(updatedUser);
	}

	function handleApplyAll() {
		if (!currentUser) {
			return;
		}

		setApplyToAllItems(true);
		applyToAll({
			variables: {
				input: {
					userId: currentUser.userId,
					agreementTag: currentUser.tagType,
				},
			},
		});
	}

	const assigneeStatusComponent = (selValue: SelectableValue, idx: number) => {
		return (
			<MenuItem key={idx} value={selValue.value}>
				<Box className={menuItemContent}>
					<Typography variant="body2">{selValue.label}</Typography>
					<Typography variant="body2" className={optionDescription}>
						{t(assigneeStatusDescriptions[selValue.value])}
					</Typography>
				</Box>
			</MenuItem>
		);
	};

	return (
		<>
			<tr key={index} style={{ backgroundColor: groupId % 2 === 0 ? "#fff" : "#f5f8f9" }}>
				<td>
					<Box display="flex" alignItems="center">
						<Typography variant="body2">
							{currentUser.lastName} {currentUser.firstName}
						</Typography>
					</Box>
				</td>
				<td>
					<Box display="flex" flexGrow={1} alignItems="center">
						<LyfeAgreementTag hideTooltip title={currentUser.tagType} height={20} />
					</Box>
				</td>
				<td>
					<TextField
						variant="standard"
						select
						value={currentUser.role}
						onChange={(event) => handleUserChanges(event)}
						SelectProps={{
							MenuProps: {
								PaperProps: {
									style: {
										borderTop: "solid 2px #367BF5",
										borderRadius: "5px",
									},
								},
							},
							native: false,
							renderValue: (v: any) => <Typography variant="body2">{t(userRoleLabels[v])}</Typography>,
						}}
						InputProps={{ disableUnderline: true }}
					>
						{roles.map((o, ii) => (
							<MenuItem key={ii} value={o.value}>
								<Box key={ii} className={menuItemContent}>
									<Typography variant="body2">{t(userRoleLabels[o.value])}</Typography>
									<Typography variant="body2" className={optionDescription}>
										{t(userRoleDescriptions[o.value])}
									</Typography>
								</Box>
							</MenuItem>
						))}
					</TextField>
				</td>
				<td>
					<LyfeTextField
						variant="standard"
						select
						value={currentUser.isAgreementAssignee ? "yes" : "no"}
						onChange={(event) => handleUserAssigneeChanges(event)}
						disabled={isDisabled() || currentUser.role === AgreementUserRole.Editor}
						InputProps={{ disableUnderline: true }}
						options={assigneeStatusOptions}
					/>
				</td>
				<td>
					<TextField
						variant="standard"
						select
						value={currentUser.caseAssignee}
						onChange={(e) => handleCaseAssigneeChanges(e)}
						disabled={isDisabled()}
						SelectProps={{
							MenuProps: {
								PaperProps: {
									style: {
										borderTop: "solid 2px #367BF5",
										borderRadius: "5px",
									},
								},
							},
							native: false,
							renderValue: (v: any) => <Typography variant="body2">{t(assigneeStatusLabels[v])}</Typography>,
						}}
						InputProps={{ disableUnderline: true }}
					>
						{caseAssigneeOptions.map((obj: any, idx: any) => assigneeStatusComponent(obj, idx))}
					</TextField>
				</td>
				<td>
					<TextField
						variant="standard"
						select
						value={currentUser.documentAssignee}
						disabled={isDisabled()}
						onChange={(event) => handleDocumentAssigneeChanges(event)}
						SelectProps={{
							MenuProps: {
								PaperProps: {
									style: {
										borderTop: "solid 2px #367BF5",
										borderRadius: "5px",
									},
								},
							},
							native: false,
							renderValue: (val: any) => <Typography variant="body2">{t(assigneeStatusLabels[val])}</Typography>,
						}}
						InputProps={{ disableUnderline: true }}
					>
						{documentAssigneeOptions.map((obj: any, idx: any) => assigneeStatusComponent(obj, idx))}
					</TextField>
				</td>
				<td>
					<Button
						style={{
							color: applyToAllItems ? "#969392" : "#367BF5",
						}}
						className={applyToAllButton}
						disabled={applyToAllItems}
						onClick={handleApplyAll}
					>
						{t("apply-to-all")}
					</Button>
				</td>
			</tr>
		</>
	);
};

export default DefaultAgreementRolesRow;
